import type React from 'react';
import { useMemo } from 'react';

import { useSandbox } from './hooks/useSandbox';
import { useWindowMessage } from './hooks/useWindowMessage';
import type { SandboxContract } from './types';

type SubmitAction = SandboxContract['actions']['submit'];
type SubmitParams = Parameters<SubmitAction>[0];

type SandboxIframeProps = {
	src: string;
	/** a11y — the iframe needs an accessible name. */
	title: string;
	/**
	 * Overrides how the child's `submit` message is handled. Receives the raw params
	 * plus the real `submit` action, and decides if/when to call it — e.g. to gate on
	 * an async step (a mutation) before committing to the state machine. When omitted,
	 * `submit` is called directly, same as every other sandbox action.
	 */
	onSubmit?: (params: SubmitParams, submit: SubmitAction) => void | Promise<void>;
	/**
	 * Hides the iframe (native `hidden` attribute) without unmounting it, so its document
	 * — and any in-memory state the child holds, e.g. conversation history — survives
	 * whatever's being shown in front of it (a loading state, an overlay, etc.).
	 */
	hidden?: boolean;
};

/**
 * iframe sandbox boundary and container. Wires the fixed sandbox actions to the
 * state machine and renders the iframe; the iframe (a separate document) reaches
 * page state only through those messages. Initial data is baked into `src` by
 * whatever renders this.
 *
 * The child origin messages are trusted from is derived from `src` itself, so
 * whatever renders this is the single source of truth for which child it embeds.
 */
export function SandboxIframe({ src, title, onSubmit, hidden }: SandboxIframeProps): React.ReactElement {
	const { actions } = useSandbox(onSubmit);
	const childOrigin = useMemo(() => new URL(src).origin, [src]);

	useWindowMessage('submit', childOrigin, actions.submit);

	useWindowMessage('back', childOrigin, actions.back);

	useWindowMessage('update', childOrigin, actions.update);

	useWindowMessage('track', childOrigin, actions.track);

	return (
		<iframe
			className="h-[calc(100vh-60px)] w-full"
			src={src}
			title={title}
			sandbox="allow-forms allow-scripts allow-same-origin"
			referrerPolicy="strict-origin-when-cross-origin"
			hidden={hidden}
		/>
	);
}
